import { getI18n, useTranslation } from "react-i18next";
import { iTxt } from "@duosoftbg/nacid-components";
import React from "react";
import "./stylesTableQF.css";
import { ReactComponent as TableDashedIcon } from "./icons/table-dashed.svg";
import { ReactComponent as LayoutGrid } from "./icons/layout-grid.svg";
import { ReactComponent as ButtonUp } from "./icons/ButtonUp.svg";
import { ReactComponent as ButtonDown } from "./icons/ButtonDown.svg";
import { CountryDetailsLong } from "../../../types/QFDataTypes";
import { mainHeadersV, sideHeadersV, subHeaders1, subHeaders2 } from "./dataTableQf";
import { getColor } from "./stylesTableQfTS";

interface QFTableVerticalProps {
  countryData: CountryDetailsLong;
  handleTables: (direction: "H" | "V") => void;
  setActiveLevelV: (value: number) => void;
  setHideMainHeaderV: (value: boolean[]) => void;
  setHideSideHeaderV: (value: boolean[]) => void;
  activeLevelV: number;
  hideMainHeaderV: boolean[];
  hideSideHeaderV: boolean[];
}

export const QFTableVertical = ({
  countryData,
  handleTables,
  setActiveLevelV,
  setHideMainHeaderV,
  setHideSideHeaderV,
  activeLevelV,
  hideMainHeaderV,
  hideSideHeaderV,
}: QFTableVerticalProps) => {
  const { t } = useTranslation();
  const reversedData = countryData.countriesQF.map((_, i, reverseData) => reverseData[reverseData.length - 1 - i]);
  const activeLevelData = reversedData.find((x) => x.level === activeLevelV);
  const maxCommonCellWidth = `auto`;

  const handleActiveLevelV = (index: number) => {
    setActiveLevelV(index);
  };

  const handleMainHeaderV = (index: number) => {
    //@ts-ignore
    setHideMainHeaderV((prev: boolean[]) => {
      return prev.map((val, i) => (i === index ? !val : val));
    });
  };

  const handleSideHeaderV = (index: number) => {
    //@ts-ignore
    setHideSideHeaderV((prev: boolean[]) => {
      return prev.map((val, i) => (i === index ? !val : val));
    });
  };
  const isWholeTableHiddenV = hideMainHeaderV.every((x) => x === true) && hideSideHeaderV.every((x) => x === true);

  const handleAllRowsV = () => {
    setHideMainHeaderV(Array(2).fill(!isWholeTableHiddenV));
    setHideSideHeaderV(Array(2).fill(!isWholeTableHiddenV));
  };

  return (
    <React.Fragment key="table-fragment">
      <div key="table-root" className="table fade-slide" style={{ gridTemplateColumns: "repeat(4, auto)" }}>
        {/* 2 table-control buttons */}
        <div key="first-row" className="control-cell fade-slide" style={{ gridColumn: "span 4", gridRow: "span 1" }}>
          <div className="row-inner">
            <div className="iconPop-V-cell">
              <TableDashedIcon onClick={() => handleTables("H")} />
            </div>
            <div className="iconPush-V-cell">
              <LayoutGrid onClick={() => handleTables("V")} />
            </div>

            {/* 3 control-push-cells */}
            <div className="control-cell fade-slide" style={{ gridColumn: `span 4`, gridRow: `span 1` }}>
              {reversedData.map((countryQF, qfIndex) => (
                <div
                  key={`btnU-${countryQF.eqfLevel.id}-${qfIndex}`}
                  className={countryQF.level === activeLevelV ? "common-V-push-active-cell" : "common-V-push-cell"}
                  style={{
                    backgroundColor: getColor(countryQF.eqfLevel.id),
                  }}
                  onClick={() => handleActiveLevelV(reversedData[qfIndex]?.eqfLevel?.id)}
                >
                  <div style={{ fontWeight: `bold` }}>
                    {iTxt(getI18n().language, countryQF.eqfLevel?.name, countryQF.eqfLevel?.nameEn)}
                  </div>
                </div>
              ))}
            </div>

            {/*Whole table control-button*/}
            <div className="iconButtonUp-VS-cell" style={{ backgroundColor: "#fff" }}>
              {!isWholeTableHiddenV ? (
                <ButtonUp className="iconButtonUp-VV-cell" onClick={() => handleAllRowsV()} />
              ) : (
                <ButtonDown className="iconButtonUp-VV-cell" onClick={() => handleAllRowsV()} />
              )}
            </div>
          </div>
        </div>

        {/* 1st main header */}
        <div
          key="header-main-1"
          className="header-V-cell"
          style={{ backgroundColor: getColor(1), gridColumn: `span 4` }}
        >
          {mainHeadersV(t, countryData, false)[0]}
          {!hideMainHeaderV[0] ? (
            <ButtonUp className="iconButtonUp-V-cell" onClick={() => handleMainHeaderV(0)} />
          ) : (
            <ButtonDown className="iconButtonUp-V-cell" onClick={() => handleMainHeaderV(0)} />
          )}
        </div>

        {/* 1st sub headers */}
        {!hideMainHeaderV[0] &&
          subHeaders1(t).map((subHeader, index) => (
            <div
              key={`subheader-1-${index}`}
              className="subheader-V-cell fade-slide"
              style={{
                backgroundColor: getColor(2),
                gridColumn: `span 1`,
                gridRow: `span 1`,
                minWidth: maxCommonCellWidth,
              }}
            >
              {subHeader}
            </div>
          ))}

        {/* 1st info fields */}
        {!hideMainHeaderV[0] && (
          <>
            <div
              key={`cellA-${activeLevelV}`}
              className="common-V-cell fade-slide"
              style={{
                backgroundColor: getColor(activeLevelData.eqfLevel.id),
                gridColumn: `span 1`,
                gridRow: `span 1`,
                minWidth: maxCommonCellWidth,
              }}
            >
              <div style={{ fontWeight: "600" }}>
                {iTxt(getI18n().language, activeLevelData.name, activeLevelData.nameEn)}
              </div>
              <div>{iTxt(getI18n().language, activeLevelData.description, activeLevelData.descriptionEn)}</div>
            </div>

            <div
              key={`cellB-${activeLevelV}`}
              className="common-V-cell fade-slide"
              style={{
                backgroundColor: getColor(activeLevelData.eqfLevel.id),
                gridColumn: `span 1`,
                gridRow: `span 1`,
                minWidth: maxCommonCellWidth,
              }}
            >
              {activeLevelData.nameNative}
            </div>

            <div
              key={`cellC-${activeLevelV}`}
              className="common-V-cell fade-slide"
              style={{
                backgroundColor: getColor(activeLevelData.eqfLevel.id),
                gridColumn: `span 1`,
                gridRow: `span 1`,
                minWidth: maxCommonCellWidth,
              }}
            >
              {activeLevelData.credits ? `${activeLevelData.credits} ${countryData.creditsLabel}` : "-"}
            </div>

            <div
              key={`cellD-${activeLevelV}`}
              className="common-V-cell fade-slide"
              style={{
                backgroundColor: getColor(activeLevelData.eqfLevel.id),
                gridColumn: `span 1`,
                gridRow: `span 1`,
                minWidth: maxCommonCellWidth,
              }}
            >
              {activeLevelData.duration ? `${activeLevelData.duration}${iTxt(getI18n().language, " г.", " y")}` : "-"}
            </div>
          </>
        )}

        {/* 2nd main header */}
        <div
          key="header-main-2"
          className="header-V-cell"
          style={{
            backgroundColor: getColor(1),
            gridColumn: `span 4`,
            gridRow: `span 1`,
          }}
        >
          {mainHeadersV(t, countryData, false)[1]}
          {!hideMainHeaderV[1] ? (
            <ButtonUp className="iconButtonUp-V-cell" onClick={() => handleMainHeaderV(1)} />
          ) : (
            <ButtonDown className="iconButtonUp-V-cell" onClick={() => handleMainHeaderV(1)} />
          )}
        </div>

        {/* 2nd side header */}
        {!hideMainHeaderV[1] &&
          subHeaders2(t).map((subHeader, index) => (
            <div
              key={`subheader-2-${index}`}
              className="subheader-V-cell"
              style={{
                backgroundColor: getColor(2),
                gridColumn: `span 1`,
                gridRow: `span 1`,
                minWidth: maxCommonCellWidth,
              }}
            >
              {subHeader}
            </div>
          ))}

        {/* 2nd info fields */}
        {!hideMainHeaderV[1] &&
          activeLevelData.levels.map((level, lIndex) => (
            <React.Fragment key={`level-row-${lIndex}`}>
              <div
                key={`cellA-${activeLevelV}-${lIndex}`}
                className="common-V-cell fade-slide"
                style={{
                  backgroundColor: getColor(activeLevelData.eqfLevel.id),
                  gridColumn: `span 1`,
                  gridRow: `span 1`,
                  minWidth: maxCommonCellWidth,
                }}
              >
                <div style={{ fontWeight: "400" }}>{iTxt(getI18n().language, level.name, level.nameEn)}</div>
              </div>

              <div
                key={`cellB-${activeLevelV}-${lIndex}`}
                className="common-V-cell fade-slide"
                style={{
                  backgroundColor: getColor(activeLevelData.eqfLevel.id),
                  gridColumn: `span 1`,
                  gridRow: `span 1`,
                  minWidth: maxCommonCellWidth,
                }}
              >
                {level.nameNative}
              </div>

              <div
                key={`cellC-${activeLevelV}-${lIndex}`}
                className="common-V-cell fade-slide"
                style={{
                  backgroundColor: getColor(activeLevelData.eqfLevel.id),
                  gridColumn: `span 1`,
                  gridRow: `span 1`,
                  minWidth: maxCommonCellWidth,
                }}
              >
                {level.credits ? `${level.credits} ${countryData.creditsLabel}` : "-"}
              </div>

              <div
                key={`cellD-${activeLevelV}-${lIndex}`}
                className="common-V-cell fade-slide"
                style={{
                  backgroundColor: getColor(activeLevelData.eqfLevel.id),
                  gridColumn: `span 1`,
                  gridRow: `span 1`,
                  minWidth: maxCommonCellWidth,
                }}
              >
                {level.duration ? `${level.duration}${iTxt(getI18n().language, " г.", " y")}` : "-"}
              </div>
            </React.Fragment>
          ))}

        {/* EQF / Bologna */}
        <div
          key="header-eqf"
          className="header-V-cell"
          style={{ backgroundColor: getColor(1), gridColumn: `span 4`, gridRow: `span 1` }}
        >
          {sideHeadersV(t, false)[0]}
          {!hideSideHeaderV[0] ? (
            <ButtonUp className="iconButtonUp-V-cell" onClick={() => handleSideHeaderV(0)} />
          ) : (
            <ButtonDown className="iconButtonUp-V-cell" onClick={() => handleSideHeaderV(0)} />
          )}
        </div>

        {!hideSideHeaderV[0] && (
          <>
            <div
              key={`eqf-cell-${activeLevelV}`}
              className="common-V-cell fade-slide"
              style={{
                backgroundColor: getColor(activeLevelData.eqfLevel.id),
                gridColumn: `span 4`,
                gridRow: `span 1`,
              }}
            >
              <div style={{ fontWeight: `bold` }}>
                {iTxt(getI18n().language, activeLevelData?.eqfLevel?.name, activeLevelData?.eqfLevel?.nameEn)}
              </div>
              <div>
                {iTxt(
                  getI18n().language,
                  activeLevelData?.eqfLevel?.description,
                  activeLevelData?.eqfLevel?.descriptionEn,
                )}
              </div>
            </div>
          </>
        )}

        <div
          key="header-bologna"
          className="header-V-cell"
          style={{ backgroundColor: getColor(1), gridColumn: `span 4`, gridRow: `span 1` }}
        >
          {sideHeadersV(t, false)[1]}
          {!hideSideHeaderV[1] ? (
            <ButtonUp className="iconButtonUp-V-cell" onClick={() => handleSideHeaderV(1)} />
          ) : (
            <ButtonDown className="iconButtonUp-V-cell" onClick={() => handleSideHeaderV(1)} />
          )}
        </div>

        {!hideSideHeaderV[1] && (
          <>
            <div
              key={`bologna-cell-${activeLevelV}`}
              className="common-V-cell fade-slide"
              style={{
                backgroundColor: getColor(activeLevelData.eqfLevel.id),
                gridColumn: `span 4`,
                gridRow: `span 1`,
              }}
            >
              <div style={{ fontWeight: `bold` }}>
                {iTxt(getI18n().language, activeLevelData?.bolognaCycle?.name, activeLevelData?.bolognaCycle?.nameEn)}
              </div>
              <div>
                {iTxt(
                  getI18n().language,
                  activeLevelData?.bolognaCycle?.description,
                  activeLevelData?.bolognaCycle?.descriptionEn,
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </React.Fragment>
  );
};
